import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * ActivityDetailModal - Full activity detail popup.
 *
 * Lazy-fetches `GET /api/activities/:id/detail` and renders the row with all
 * UUID-shaped metadata fields swapped for resolved entity names. Below 640px
 * the modal becomes a full-screen sheet with a sticky header that keeps the
 * close button on-screen no matter how much content scrolls (this was the
 * mobile bug — the corner X could be lost under the iOS notch).
 */
import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { ActivityDetailView } from './ActivityDetailView';
import { isInvoiceRuleActivityType } from './details/invoiceRuleActivity';
import { activityTypeLabel } from '../../types/activity';
import { X } from 'lucide-react';
const colors = {
    background: {
        primary: '#111827',
        secondary: '#1F2937',
        overlay: 'rgba(0, 0, 0, 0.75)',
    },
    text: {
        primary: '#FFFFFF',
        secondary: '#9CA3AF',
        tertiary: '#6B7280',
    },
    border: {
        default: '#374151',
    },
    accent: {
        blue: '#3B82F6',
    },
};
// Pretty-print snake/dot-separated keys: "work_order_display_id" → "Work Order Display Id";
// strip "_id" suffix since the resolved value carries the name, not the id.
function prettyLabel(key) {
    return key
        .replace(/_id$/, '')
        .split(/[._]/)
        .filter(Boolean)
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join(' ');
}
// Format an arbitrary metadata value for display. UUIDs that match an entry
// in the resolved-refs lookup table are swapped for the display name with the
// id shown as a small subtitle.
function formatValue(value, refsById) {
    if (value === null || value === undefined || value === '') {
        return _jsx("span", { style: { color: colors.text.tertiary }, children: "\u2014" });
    }
    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }
    if (typeof value === 'number') {
        return String(value);
    }
    if (typeof value === 'string') {
        const ref = refsById.get(value);
        if (ref) {
            return (_jsxs("span", { children: [_jsx("strong", { style: { color: colors.text.primary }, children: ref.display_name }), _jsx("span", { style: { color: colors.text.tertiary, fontSize: 10, marginLeft: 6 }, children: prettyLabel(ref.type) })] }));
        }
        return value;
    }
    if (Array.isArray(value)) {
        if (value.length === 0)
            return _jsx("span", { style: { color: colors.text.tertiary }, children: "[]" });
        return (_jsx("span", { children: value.map((item, i) => (_jsxs("span", { children: [i > 0 && ', ', formatValue(item, refsById)] }, i))) }));
    }
    if (typeof value === 'object') {
        return (_jsx("div", { style: { display: 'grid', gridTemplateColumns: '1fr 2fr', columnGap: 8, rowGap: 2, marginTop: 4 }, children: Object.entries(value).map(([k, v]) => (_jsxs(React.Fragment, { children: [_jsxs("span", { style: { color: colors.text.tertiary, fontSize: 11 }, children: [prettyLabel(k), ":"] }), _jsx("span", { style: { color: colors.text.primary, fontSize: 12 }, children: formatValue(v, refsById) })] }, k))) }));
    }
    return String(value);
}
// Walk metadata and emit a flat list of {label, value} entries. Nested objects
// are surfaced via formatValue's recursive rendering so the operator sees
// every level without us having to flatten path-style.
function buildDetailRows(metadata) {
    if (!metadata)
        return [];
    const rows = [];
    for (const [k, v] of Object.entries(metadata)) {
        // Skip noisy framework-injected keys that aren't useful to operators:
        //  - event_id / aggregate_type / version: from NewActivityFromEvent's
        //    envelope, not the actual change
        //  - original_data: verbatim event payload — already exposed via the
        //    top-level resource + the flat metadata keys
        //  - work_order_display_id / location_id: redundant with the resolved
        //    Resource / Location at the top of the modal
        if (k === 'event_id' ||
            k === 'aggregate_type' ||
            k === 'version' ||
            k === 'original_data' ||
            k === 'work_order_display_id' ||
            k === 'location_id') {
            continue;
        }
        rows.push([k, v]);
    }
    return rows;
}
// formatActivityTypeTitle renders the activity_type slug as a human-readable
// title, dropping the redundant aggregate prefix when it would just repeat
// the resource type. e.g. "work_order.department_state_changed" →
// "Department State Changed" (and the modal already shows it's a work-order
// row via the resource link). For top-level slugs without a dot it's a
// plain Title Case.
function formatActivityTypeTitle(slug) {
    const label = activityTypeLabel(slug);
    if (label)
        return label;
    const parts = slug.split('.');
    // Drop the aggregate prefix if there's at least one more segment.
    const display = parts.length > 1 ? parts.slice(1).join(' ') : slug;
    return display
        .replace(/_/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
}
export const ActivityDetailModal = ({ isOpen, activity, activityId, fetchDetail, onClose, onWorkOrderClick, onUserClick, }) => {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        if (typeof window === 'undefined')
            return;
        const update = () => setIsMobile(window.innerWidth < 640);
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') {
            onClose();
        }
    }, [onClose]);
    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        };
    }, [isOpen, handleKeyDown]);
    useEffect(() => {
        if (!isOpen) {
            setDetail(null);
            setError(null);
            setLoading(false);
            return;
        }
        const id = activityId || activity?.id;
        if (!id || !fetchDetail) {
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        fetchDetail(id)
            .then((payload) => {
            if (!cancelled) {
                setDetail(payload);
                setLoading(false);
            }
        })
            .catch((err) => {
            if (!cancelled) {
                setError(err instanceof Error ? err.message : 'Failed to load detail');
                setLoading(false);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [isOpen, activityId, activity?.id, fetchDetail]);
    // Lookup table id→ref so formatValue can swap UUIDs anywhere in metadata
    // for their human-readable name, not just on allowlisted keys.
    const refsById = useMemo(() => {
        const map = new Map();
        for (const r of detail?.resolved_refs ?? []) {
            map.set(r.id, r);
        }
        return map;
    }, [detail?.resolved_refs]);
    if (!isOpen) {
        return null;
    }
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };
    const renderedActivity = detail?.activity ?? activity ?? null;
    // Invoice rule changes are fully laid out by their own renderer (who, where,
    // before → after); the raw catch-all would only repeat it as nested data.
    const detailRows = renderedActivity && !isInvoiceRuleActivityType(renderedActivity.activity_type) ? buildDetailRows(renderedActivity.metadata) : [];
    const overlayStyle = {
        ...styles.overlay,
        padding: isMobile ? 0 : '16px',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: isMobile ? 'stretch' : 'center',
    };
    const modalStyle = {
        ...styles.modal,
        maxWidth: isMobile ? '100%' : '720px',
        maxHeight: isMobile ? '100%' : '90vh',
        borderRadius: isMobile ? 0 : 12,
        height: isMobile ? '100%' : 'auto',
    };
    return (_jsx("div", { style: overlayStyle, onClick: handleBackdropClick, children: _jsxs("div", { style: modalStyle, children: [_jsxs("div", { style: styles.header, children: [_jsx("div", { style: styles.headerTitle, children: renderedActivity ? formatActivityTypeTitle(renderedActivity.activity_type) : 'Activity' }), _jsx("button", { style: styles.closeButton, onClick: onClose, "aria-label": "Close", children: _jsx(X, { size: 22 }) })] }), _jsx("div", { style: styles.content, children: !renderedActivity ? (_jsx("div", { style: styles.placeholder, children: loading ? 'Loading…' : 'No activity selected.' })) : (_jsxs(_Fragment, { children: [_jsx(ActivityDetailView, { activity: renderedActivity, resolvedRefs: detail?.resolved_refs, importDetails: detail?.import_details, importDetailsTotal: detail?.import_details_total, loadingDetail: loading, detailError: error, onClose: onClose, onWorkOrderClick: onWorkOrderClick, onUserClick: onUserClick }), detailRows.length > 0 && (_jsxs("div", { style: styles.detailsPanel, children: [_jsx("div", { style: styles.sectionTitle, children: "All Details" }), _jsx("dl", { style: styles.detailList, children: detailRows.map(([key, value]) => (_jsxs("div", { style: styles.detailRow, children: [_jsx("dt", { style: styles.detailLabel, children: prettyLabel(key) }), _jsx("dd", { style: styles.detailValue, children: formatValue(value, refsById) })] }, key))) })] }))] })) }), isMobile && (_jsx("button", { style: styles.bottomCloseBar, onClick: onClose, "aria-label": "Close", children: "Close" }))] }) }));
};
const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: colors.background.overlay,
        display: 'flex',
        zIndex: 1000,
    },
    modal: {
        position: 'relative',
        width: '100%',
        backgroundColor: colors.background.primary,
        border: `1px solid ${colors.border.default}`,
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        backgroundColor: colors.background.secondary,
        borderBottom: `1px solid ${colors.border.default}`,
        position: 'sticky',
        top: 0,
        zIndex: 5,
    },
    headerTitle: {
        color: colors.text.primary,
        fontSize: 13,
        fontWeight: 600,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        paddingRight: 12,
    },
    closeButton: {
        width: 44,
        height: 44,
        minWidth: 44,
        minHeight: 44,
        background: 'rgba(255, 255, 255, 0.06)',
        border: `1px solid ${colors.border.default}`,
        color: colors.text.primary,
        cursor: 'pointer',
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background-color 0.2s, color 0.2s',
    },
    content: {
        overflowY: 'auto',
        flex: 1,
    },
    placeholder: {
        padding: 40,
        textAlign: 'center',
        color: colors.text.secondary,
    },
    detailsPanel: {
        padding: '12px 16px 16px 16px',
        borderTop: `1px solid ${colors.border.default}`,
        backgroundColor: colors.background.secondary,
    },
    sectionTitle: {
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: colors.text.tertiary,
        marginBottom: 8,
    },
    detailList: {
        margin: 0,
        display: 'grid',
        gridTemplateColumns: 'minmax(140px, 1fr) 2fr',
        rowGap: 8,
        columnGap: 12,
    },
    detailRow: {
        display: 'contents',
    },
    detailLabel: {
        color: colors.text.tertiary,
        fontSize: 12,
        paddingTop: 2,
    },
    detailValue: {
        margin: 0,
        color: colors.text.primary,
        fontSize: 12,
        wordBreak: 'break-word',
    },
    bottomCloseBar: {
        border: 'none',
        background: colors.background.secondary,
        color: colors.text.primary,
        padding: '14px 16px',
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
        borderTop: `1px solid ${colors.border.default}`,
    },
};
export default ActivityDetailModal;
